import { Check, GripHorizontal, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ConfirmationDialog } from "../../../components/common/ConfirmationDialog";
import { useStopwatchSeconds } from "./stopwatchTick";
import {
  STOPWATCH_BUTTON_BASE,
  formatStopwatchTime,
  getStageStyle,
  getStageText,
  type RoastStage,
} from "./stopwatchHelpers";
import { MAX_ROAST_TEMP_C, clampTemperatureInput } from "./RoastProfileUtils";

export type { RoastStage };

interface Props {
  stage: RoastStage;
  onMainClick: (atSeconds: number, beanTemp: number) => void;
  onStart?: () => void;
  onPause?: () => void;
  onRestart: () => void;
  readOnly?: boolean;
  disableFloating?: boolean;
}

const StopwatchSectionComponent: React.FC<Props> = ({
  stage,
  onMainClick,
  onStart,
  onPause,
  onRestart,
  readOnly = false,
  disableFloating = false,
}) => {
  const liveSeconds = useStopwatchSeconds();
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [isTempPromptOpen, setIsTempPromptOpen] = useState(false);
  const [tempDraft, setTempDraft] = useState("");
  const [pendingSeconds, setPendingSeconds] = useState<number | null>(null);
  const [firstCrackTime, setFirstCrackTime] = useState<number | null>(null);
  const tempInputRef = useRef<HTMLInputElement | null>(null);

  // Reset development-time anchor if the roast is reset externally.
  const [prevStage, setPrevStage] = useState(stage);
  if (stage !== prevStage) {
    setPrevStage(stage);
    if (stage === "idle") setFirstCrackTime(null);
  }

  const developmentSeconds =
    firstCrackTime !== null ? Math.max(0, liveSeconds - firstCrackTime) : null;

  const [isFloating, setIsFloating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const floatingRef = useRef<HTMLDivElement | null>(null);
  const floatingPosRef = useRef({ x: 0, y: 0, initialized: false });
  const vvStateRef = useRef({ offsetX: 0, offsetY: 0, width: 0, height: 0 });

  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{
    touchId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  useEffect(() => {
    if (disableFloating) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Pada dashboard layout dengan scroll container sendiri, boundingClientRect.top
        // mungkin tidak akan pernah kurang dari 0 (karena tertutup header/parent).
        // Kita cek apakah posisinya di atas pertengahan layar saat tidak terlihat.
        if (!entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight / 2) {
          setIsFloating(true);
        } else {
          setIsFloating(false);
        }
      },
      { threshold: 0, rootMargin: "0px" }
    );

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }
    return () => observer.disconnect();
  }, [disableFloating]);



  const handleOpenTempPrompt = () => {
    if (stage === "ended" || readOnly) return;
    if (stage === "idle") {
      onStart?.();
      setPendingSeconds(0);
    } else {
      // Snapshot from the external tick store to stay accurate even when the
      // parent's `seconds` prop is only mirrored at bucket boundaries.
      setPendingSeconds(liveSeconds);
    }
    if (stage === "firstCrack") {
      // Floor ke detik bulat agar fractional ms DT selaras dengan main stopwatch
      setFirstCrackTime(Math.floor(liveSeconds));
    }
    if (stage === "endReady") {
      onPause?.();
    }
    setTempDraft("");
    setIsTempPromptOpen(true);
  };

  const handleConfirmTemp = () => {
    const parsed = tempDraft === "" ? NaN : Number(tempDraft);
    if (!Number.isFinite(parsed)) return;
    onMainClick(pendingSeconds ?? liveSeconds, parsed);
    setIsTempPromptOpen(false);
    setTempDraft("");
    setPendingSeconds(null);
  };

  const shouldFloat = isFloating && !disableFloating;

  const getViewportState = useCallback(() => {
    if (typeof window === 'undefined') return { offsetX: 0, offsetY: 0, width: 0, height: 0 };
    if (window.visualViewport) {
      return {
        offsetX: window.visualViewport.offsetLeft,
        offsetY: window.visualViewport.offsetTop,
        width: window.visualViewport.width,
        height: window.visualViewport.height,
      };
    }
    return {
      offsetX: window.scrollX,
      offsetY: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }, []);

  const syncTransform = useCallback(() => {
    const el = floatingRef.current;
    if (!el) return;
    const { offsetX, offsetY } = vvStateRef.current;
    const { x, y } = floatingPosRef.current;
    el.style.transform = `translate3d(${x + offsetX}px, ${y + offsetY}px, 0)`;
  }, []);

  const updatePosition = useCallback(() => {
    const el = floatingRef.current;
    if (!el) return;
    vvStateRef.current = getViewportState();

    const panelWidth = el.offsetWidth;
    const panelHeight = el.offsetHeight;
    const { width, height } = vvStateRef.current;

    // Initialize if first time
    if (!floatingPosRef.current.initialized) {
      floatingPosRef.current = {
        x: width - panelWidth - 16,
        y: 80,
        initialized: true
      };
    }

    // Clamp to visual viewport
    const padding = 8;
    const minX = padding;
    const minY = padding;
    const maxX = Math.max(padding, width - panelWidth - padding);
    const maxY = Math.max(padding, height - panelHeight - padding);

    floatingPosRef.current.x = Math.min(Math.max(floatingPosRef.current.x, minX), maxX);
    floatingPosRef.current.y = Math.min(Math.max(floatingPosRef.current.y, minY), maxY);

    syncTransform();
  }, [getViewportState, syncTransform]);

  useEffect(() => {
    if (!shouldFloat) {
      dragStateRef.current = null;
      return;
    }

    // Call updatePosition on mount or activation of the floating panel
    updatePosition();

    const onUpdate = updatePosition;

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onUpdate);
      window.visualViewport.addEventListener("scroll", onUpdate);
      return () => {
        window.visualViewport?.removeEventListener("resize", onUpdate);
        window.visualViewport?.removeEventListener("scroll", onUpdate);
      };
    } else {
      window.addEventListener("resize", onUpdate);
      window.addEventListener("scroll", onUpdate);
      return () => {
        window.removeEventListener("resize", onUpdate);
        window.removeEventListener("scroll", onUpdate);
      };
    }
  }, [shouldFloat, updatePosition]);

  useEffect(() => {
    if (!isTempPromptOpen) {
      if (shouldFloat) updatePosition();
      return;
    }
    tempInputRef.current?.focus();
    if (shouldFloat) updatePosition();
  }, [isTempPromptOpen, shouldFloat, updatePosition]);

  // Shared clamp + transform for an active drag, driven by either a touch point
  // or the mouse (visual-viewport relative).
  const applyPointerPosition = useCallback((clientX: number, clientY: number) => {
    const state = dragStateRef.current;
    const el = floatingRef.current;
    if (!state || !el) return;

    const dx = clientX - state.startX;
    const dy = clientY - state.startY;

    const panelWidth = el.offsetWidth;
    const panelHeight = el.offsetHeight;
    const { width, height } = vvStateRef.current;

    const padding = 8;
    const minX = padding;
    const minY = padding;
    const maxX = Math.max(padding, width - panelWidth - padding);
    const maxY = Math.max(padding, height - panelHeight - padding);

    floatingPosRef.current.x = Math.min(Math.max(state.originX + dx, minX), maxX);
    floatingPosRef.current.y = Math.min(Math.max(state.originY + dy, minY), maxY);

    syncTransform();
  }, [syncTransform]);

  const handleDragTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!shouldFloat) return;
    const touch = e.touches[0];
    const el = floatingRef.current;
    if (!touch || !el) return;

    vvStateRef.current = getViewportState();

    dragStateRef.current = {
      touchId: touch.identifier,
      startX: touch.clientX,
      startY: touch.clientY,
      originX: floatingPosRef.current.x,
      originY: floatingPosRef.current.y,
    };
    setIsDragging(true);
  }, [shouldFloat, getViewportState]);

  const handleDragTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state) return;

    const touch = Array.from(e.touches).find((entry) => entry.identifier === state.touchId);
    if (!touch) return;

    applyPointerPosition(touch.clientX, touch.clientY);
  }, [applyPointerPosition]);

  const handleDragTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state) return;

    const stillActive = Array.from(e.touches).some((entry) => entry.identifier === state.touchId);
    if (stillActive) return;

    dragStateRef.current = null;
    setIsDragging(false);
  }, []);

  // Desktop drag. A mouse can travel outside the panel, so move/up are bound to
  // `window` for the lifetime of the drag. touchId === -1 marks a mouse drag.
  const handleDragMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!shouldFloat) return;
    const el = floatingRef.current;
    if (!el) return;
    e.preventDefault();

    vvStateRef.current = getViewportState();

    dragStateRef.current = {
      touchId: -1,
      startX: e.clientX,
      startY: e.clientY,
      originX: floatingPosRef.current.x,
      originY: floatingPosRef.current.y,
    };
    setIsDragging(true);
  }, [shouldFloat, getViewportState]);

  useEffect(() => {
    if (!isDragging) return;
    const state = dragStateRef.current;
    if (!state || state.touchId !== -1) return;

    const onMove = (e: MouseEvent) => applyPointerPosition(e.clientX, e.clientY);
    const onUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, applyPointerPosition]);

  const renderStopwatchContent = (floating: boolean) => (
    <>
      {!floating && (
        <h3 className="text-md font-medium text-gray-900 mb-4 border-b pb-2">
          Stopwatch
        </h3>
      )}

      <div className={`grid items-center ${floating ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-6'}`}>
        <div className={floating ? 'text-center mb-1' : 'order-1 md:order-2 text-center md:text-right'}>
          <div className={`${floating ? 'text-2xl' : 'text-4xl'} font-mono text-gray-900 tracking-wider`}>
            {formatStopwatchTime(liveSeconds)}
          </div>
          {developmentSeconds !== null && (
            <div
              className={`${floating ? 'text-sm' : 'text-xl'} font-mono text-amber-700 tracking-wider flex items-center justify-center md:justify-end gap-1.5`}
              data-test-id="stopwatch-development-time"
            >
              <span className={`${floating ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-widest text-amber-600/80`}>DT</span>
              {formatStopwatchTime(developmentSeconds)}
            </div>
          )}
        </div>

        <div className={`flex items-center ${floating ? 'justify-center gap-2' : 'order-2 md:order-1 justify-center md:justify-start gap-3'}`}>
          {isTempPromptOpen ? (
            <div className={`${STOPWATCH_BUTTON_BASE} ${floating ? 'w-40 h-10' : 'w-64'} px-2 gap-2 ${getStageStyle(stage)}`}>
              <input
                ref={tempInputRef}
                type="number"
                inputMode="numeric"
                autoComplete="off"
                max={MAX_ROAST_TEMP_C}
                value={tempDraft}
                onChange={(e) => setTempDraft(clampTemperatureInput(e.target.value))}
                onKeyDownCapture={(e) => {
                  if ((e as React.KeyboardEvent<HTMLInputElement>).nativeEvent.isComposing) return;
                  if (
                    e.key === "Enter" ||
                    e.key === "NumpadEnter" ||
                    e.key === "Return" ||
                    e.code === "Enter" ||
                    e.code === "NumpadEnter" ||
                    (e as unknown as { keyCode?: number }).keyCode === 13
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleConfirmTemp();
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" ||
                    e.key === "NumpadEnter" ||
                    e.key === "Return" ||
                    e.code === "Enter" ||
                    e.code === "NumpadEnter" ||
                    (e as unknown as { keyCode?: number }).keyCode === 13
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleConfirmTemp();
                  }
                }}
                placeholder="Bean temp (°C)"
                className={`${floating ? 'h-8 text-xs' : 'h-10 text-sm'} w-full rounded-md bg-white px-2 text-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              />
              <button
                type="button"
                onClick={handleConfirmTemp}
                disabled={tempDraft === "" || !Number.isFinite(Number(tempDraft))}
                className={`${floating ? 'h-8 w-8' : 'h-10 w-10'} shrink-0 rounded-md bg-gray-900 text-white transition-colors hover:bg-gray-800 disabled:opacity-50 disabled:hover:bg-gray-900 flex items-center justify-center`}
              >
                <Check className={floating ? "w-4" : "w-5"} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleOpenTempPrompt}
              disabled={stage === "ended" || readOnly}
              className={`${STOPWATCH_BUTTON_BASE} ${floating ? 'w-40 h-10 text-sm' : 'w-64'} ${getStageStyle(stage)} ${readOnly ? 'cursor-not-allowed opacity-80' : ''}`}
            >
              {getStageText(stage)}
            </button>
          )}

          {stage !== "idle" && !readOnly && (
            <button
              type="button"
              onClick={() => setShowRestartConfirm(true)}
              className={`${STOPWATCH_BUTTON_BASE} ${floating ? 'w-10 h-10' : 'w-32'} bg-red-400 hover:bg-red-600 text-white`}
            >
              <RotateCcw className={floating ? "w-4" : "w-5"}/>
            </button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Static Container */}
      <div className={`bg-gray-50 p-4 rounded-lg border border-gray-200 ${shouldFloat ? 'opacity-0 pointer-events-none' : ''}`}>
        {renderStopwatchContent(false)}
      </div>

      {/* Floating Container (Mobile Only) - Using React Portal */}
      {shouldFloat && typeof document !== 'undefined' && createPortal(
          <div
            ref={(node) => {
              floatingRef.current = node;
              if (node && !floatingPosRef.current.initialized) {
                // To avoid flash on mount, we can set initial position instantly
                updatePosition();
              }
            }}
            className={`fixed z-[9999] bg-white p-3 rounded-lg shadow-2xl border border-gray-300 shadow-black/20 top-0 left-0 ${isDragging ? '' : 'transition-transform duration-200'}`}
            style={{
              touchAction: isDragging ? 'none' : 'auto'
            }}
            onTouchMove={handleDragTouchMove}
            onTouchEnd={handleDragTouchEnd}
            onTouchCancel={handleDragTouchEnd}
            data-test-id="floating-stopwatch-container"
          >
          <div
            className="mb-2 flex cursor-move select-none items-center justify-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
            onTouchStart={handleDragTouchStart}
            onMouseDown={handleDragMouseDown}
            style={{ touchAction: 'none' }}
            data-test-id="floating-stopwatch-drag-handle"
          >
            <GripHorizontal className="h-3.5 w-3.5" />
            Drag
          </div>
          {renderStopwatchContent(true)}
        </div>,
        document.body
      )}

      <ConfirmationDialog
        isOpen={showRestartConfirm}
        onClose={() => setShowRestartConfirm(false)}
        onConfirm={() => {
          setIsTempPromptOpen(false);
          setTempDraft("");
          setPendingSeconds(null);
          setFirstCrackTime(null);
          onRestart();
        }}
        title="Restart Roast"
        message="Are you sure you want to restart? All progress will be lost."
        confirmLabel="Restart"
        isDestructive={true}
      />
    </div>
  );
};

export const StopwatchSection = React.memo(StopwatchSectionComponent);
